<script setup lang="ts">

import 'swiper/css/navigation'
import 'swiper/css/controller'


import CapsulesAiTipContent from '@/components/UI/CustomAITipComponets/CapsulesAiTipContent.vue'
import CustomPopoverWithScopeIcon from '@/components/UI/CustomAITipComponets/CustomPopoverWithScopeIcon.vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import type { CapsulesPreview, Item, CapsuleItem } from '@/types/CapsulesTypes'
import { Controller, Navigation } from 'swiper/modules'
import { useRouter } from 'vue-router'

const { capsulesPreviewItem } = defineProps<{
  capsulesPreviewItem:CapsulesPreview
}>()

const router = useRouter()

const swiperModules = [ Navigation, Controller]

function getCapsuleItems(item: Item): CapsuleItem[] {
  return item.capsuleItems ?? item.capsule_items ?? []
}

function getProductImageUrl(product: CapsuleItem): string {
  return product.imageUrl ?? product.image_url ?? ''
}

function getProductId(product: CapsuleItem): number {
  return product.productId ?? product.product_id ?? 0
}

function getProductType(product: CapsuleItem): string {
  return product.productType ?? product.product_type ?? ''
}

function getCapsuleId(item: Item): number {
  return item.capsuleId ?? item.capsule_id ?? 0
}

async function goToCapsule(id:number) {
  await router.push(`/capsule=${id}`)
}

</script>

<template>
  <div class="capsules-card-wrapper">
    <swiper
      :space-between="20"
      :slidesPerGroup="1"
      :modules="swiperModules"
      :navigation="true"
      class="swiper-for-carousel-items">
      <swiper-slide v-for="capsuleItem in capsulesPreviewItem.items">
        <div class="capsule-card-main-content"
              @click="goToCapsule(capsulesPreviewItem.id)"
        >
          <div
            v-for="(productOfCapsuleItem, index) in getCapsuleItems(capsuleItem)"
            :key="getProductId(productOfCapsuleItem)"
            :class="`capsules-image ${getProductType(productOfCapsuleItem)} _2${getCapsuleId(capsuleItem) + 2}`"
            :style="{
                        backgroundImage: `url(${getProductImageUrl(productOfCapsuleItem)})`,
                      }"
          >
            <custom-popover-with-scope-icon>
              <capsules-ai-tip-content
                :price="productOfCapsuleItem.price"
                :image="getProductImageUrl(productOfCapsuleItem)"
                :brand="productOfCapsuleItem.brandName ?? productOfCapsuleItem.brand_name ?? ''"
                :title="productOfCapsuleItem.productName ?? productOfCapsuleItem.product_name ?? ''"
                :id-of-product="getProductId(productOfCapsuleItem)"
                :id-of-variant="productOfCapsuleItem.productVariantId ?? productOfCapsuleItem.product_variant_id ?? 0"
              />
            </custom-popover-with-scope-icon>
          </div>
        </div>
      </swiper-slide>
    </swiper>

    <div class="capsule-card-footer">
      <span class="capsule-card-footer-title h2">{{capsulesPreviewItem.name}}</span>
      <span class="capsule-card-footer-description body ">{{capsulesPreviewItem.description}}</span>
    </div>
  </div>
</template>

<style scoped>
.capsules-card-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  border-top-right-radius: 40px;
  border-top-left-radius: 40px;
  overflow: hidden;
}


.capsule-card-main-content {
  height: 500px;
  width: 100%;
  position: relative;
  background: linear-gradient(rgba(110, 133, 203, 0.4), rgba(230, 227, 236, 0));
}


.capsules-image {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  background-position: center center;
  background-origin: border-box;
  background-repeat: no-repeat;
  background-color: transparent;
  background-size: contain;

  filter: drop-shadow(0 5px 6px rgba(0, 0, 0, 0.2));
}


.img-on-capsule {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.capsule-card-footer {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.capsule-card-footer-title {
  color: rgba(35, 47, 69, 1);
}

.capsule-card-footer-description {
  color: rgba(35, 47, 69, 0.6);
}

.swiper-for-carousel-items {
  width: 100%;
}

:deep(.swiper-button-next,
.swiper-button-prev ) {
  opacity: 0; /* Hide buttons by default */
  transition: opacity 0.3s ease-in-out; /* Smooth transition for appearance */
}

.capsules-card-wrapper:hover :deep(.swiper-button-next, .swiper-button-prev) {
  opacity: 1; /* Show buttons on hover of the Swiper container */
}

:deep(.swiper-button-disabled) {
  opacity: 0;
}

.capsules-card-wrapper:hover :deep(.swiper-button-disabled) {
  opacity: 0.65 ;
}

:deep(.swiper-button-prev:after) {
  color:var(--primary-blue-100) ;
  font-size: 14px ;
  font-weight: 600 ;
  padding-right: 2px ;
}

:deep(.swiper-button-prev) {
  display: flex ;
  justify-content: center ;
  align-items: center ;
  width: 44px ;
  height:44px ;
  background: var(--white-100) ;
  border-radius: 50% ;
  overflow: hidden ;
}

:deep(.swiper-button-next) {
  display: flex ;
  justify-content: center ;
  align-items: center ;
  width: 44px ;
  height:44px ;
  background: var(--white-100) ;
  border-radius: 50% ;
  overflow: hidden ;
}

:deep(.swiper-button-next:after) {
  color:var(--primary-blue-100) !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  padding-left: 2px !important;
}

@media (max-width: 1720px) {
  .capsule-card-main-content {
    height: 354px;
  }
}

</style>